import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "../i18n";
import { useBooking } from "../BookingContext";
import { publicCarsApi as carsApi, publicLocationsApi as locationsApi } from "../../api";
import { categories } from "../../mock";
import SearchBar from "../components/SearchBar";
import { MapPin, Users, Briefcase, Fuel, Star, SlidersHorizontal, Loader2 } from "lucide-react";

export default function CarsList() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { pickupLocation } = useBooking();

  const [cars, setCars] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load all locations once for the filter dropdown
  useEffect(() => {
    locationsApi.list().then(setLocations).catch(() => setLocations([]));
  }, []);

  // Default the location filter to the user's chosen pickup location
  const [filters, setFilters] = useState({
    categories: [],
    location: pickupLocation || "any",
    minSeats: 0,
    maxPrice: 1000,
  });
  // Keep filter in sync if the user changes pickup in SearchBar above
  useEffect(() => {
    if (pickupLocation) setFilters((f) => ({ ...f, location: pickupLocation }));
  }, [pickupLocation]);

  // Fetch cars whenever the location filter changes.
  // The backend matches `location` against both car.pickup_location.name
  // AND car.pickup_locations[].name, so multi-location cars are visible.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const fetcher =
      filters.location && filters.location !== "any"
        ? carsApi.list({ location: filters.location })
        : carsApi.listAll();
    fetcher
      .then((c) => {
        if (!cancelled) setCars(c);
      })
      .catch((e) => {
        if (!cancelled) {
          setCars([]);
          setError(e);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters.location]);

  // Sorted, de-duplicated list of location names for the dropdown
  const locationNames = useMemo(
    () => [...new Set(locations.map((l) => l.name).filter(Boolean))].sort(),
    [locations]
  );

  const [sort, setSort] = useState("priceAsc");
  const [showFilters, setShowFilters] = useState(false);

  const toggleCategory = (c) =>
    setFilters((f) => ({
      ...f,
      categories: f.categories.includes(c) ? f.categories.filter((x) => x !== c) : [...f.categories, c],
    }));

  const filtered = useMemo(() => {
    // Location filtering happens server-side via the API query; only
    // category / seats / price are applied client-side here.
    let arr = cars.filter((c) => {
      if (filters.categories.length > 0 && !filters.categories.includes(c.category)) return false;
      if (filters.minSeats > 0 && c.seats < filters.minSeats) return false;
      if (c.price > filters.maxPrice) return false;
      return true;
    });
    if (sort === "priceAsc") arr = [...arr].sort((a, b) => a.price - b.price);
    if (sort === "priceDesc") arr = [...arr].sort((a, b) => b.price - a.price);
    if (sort === "name") arr = [...arr].sort((a, b) => a.name.localeCompare(b.name));
    return arr;
  }, [cars, filters.categories, filters.minSeats, filters.maxPrice, sort]);

  const clearFilters = () =>
    setFilters({ categories: [], location: "any", minSeats: 0, maxPrice: 1000 });

  return (
    <div>
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <SearchBar />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight">
            {filtered.length} {t("cars.results")} <span className="text-gray-400 font-medium text-base">· {pickupLocation}</span>
          </h1>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowFilters((s) => !s)} className="pill-btn outline inline-flex items-center gap-2 md:hidden" style={{ padding: "0.5rem 1rem" }}>
              <SlidersHorizontal size={14} /> {t("cars.filtersTitle")}
            </button>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <span className="font-semibold">{t("cars.sortLabel")}:</span>
              <select value={sort} onChange={(e) => setSort(e.target.value)} className="select-soft">
                <option value="priceAsc">{t("cars.sortPriceAsc")}</option>
                <option value="priceDesc">{t("cars.sortPriceDesc")}</option>
                <option value="name">{t("cars.sortName")}</option>
              </select>
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-6">
          {/* Filters */}
          <aside className={`${showFilters ? "block" : "hidden"} md:block`}>
            <div className="card-soft p-5 sticky top-24">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold">{t("cars.filtersTitle")}</h3>
                <button onClick={clearFilters} className="text-xs text-red-600 hover:text-red-700 font-semibold">{t("cars.clear")}</button>
              </div>
              <div className="mb-5">
                <p className="field-label">{t("cars.category")}</p>
                <div className="flex flex-wrap gap-2">
                  {categories.map((c) => (
                    <button key={c} onClick={() => toggleCategory(c)} className={`chip text-xs ${filters.categories.includes(c) ? "active" : ""}`} style={{ padding: "0.3rem 0.75rem" }}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mb-5">
                <p className="field-label">{t("nav.locations")}</p>
                <select
                  className="input-soft"
                  value={filters.location}
                  onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                  data-testid="location-filter"
                >
                  <option value="any">{t("cars.anyLocation")}</option>
                  {locationNames.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>
              <div className="mb-5">
                <p className="field-label">{t("cars.seats")} ≥ {filters.minSeats || "Any"}</p>
                <input type="range" min={0} max={7} value={filters.minSeats} onChange={(e) => setFilters({ ...filters, minSeats: +e.target.value })} className="w-full accent-red-600" />
              </div>
              <div>
                <p className="field-label">{t("cars.priceRange")}: $0 – ${filters.maxPrice}</p>
                <input type="range" min={50} max={1000} step={10} value={filters.maxPrice} onChange={(e) => setFilters({ ...filters, maxPrice: +e.target.value })} className="w-full accent-red-600" />
              </div>
            </div>
          </aside>

          {/* Cars grid */}
          <div>
            {error && !loading && (
              <div className="card-soft p-6 mb-4 border-l-4 border-amber-500" style={{ background: "#fffbeb" }}>
                <h3 className="font-bold text-amber-900 mb-1">Our fleet service is temporarily unavailable</h3>
                <p className="text-sm text-amber-800">
                  We can't reach our inventory system right now. Please try again in a few minutes, or contact us directly to reserve a vehicle.
                </p>
              </div>
            )}
            {loading ? (
              <div className="card-soft p-12 text-center text-gray-400 inline-flex items-center justify-center gap-2 w-full">
                <Loader2 className="animate-spin" size={18} /> Loading vehicles...
              </div>
            ) : filtered.length === 0 ? (
              <div className="card-soft p-12 text-center text-gray-500">{t("cars.noResults")}</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {filtered.map((c) => (
                  <div key={c.id} className="card-soft overflow-hidden hover:shadow-xl transition-shadow cursor-pointer group flex flex-col" onClick={() => navigate(`/cars/${c.id}`)}>
                    <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                      {c.image ? (
                        <img src={c.image} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : <div className="w-full h-full" />}
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-bold text-lg leading-tight">{c.name}</h3>
                        <div className="flex items-center gap-1 text-amber-500 text-sm font-semibold flex-shrink-0">
                          <Star size={13} fill="currentColor" /> 4.9
                        </div>
                      </div>
                      <p className="text-xs uppercase tracking-wider text-gray-500 mt-1">{c.category}</p>
                      <div className="flex items-center gap-3 text-xs text-gray-600 mt-3">
                        <span className="inline-flex items-center gap-1"><Users size={12} /> {c.seats}</span>
                        <span className="inline-flex items-center gap-1"><Briefcase size={12} /> 2</span>
                        <span className="inline-flex items-center gap-1"><Fuel size={12} /> Gas</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-3 flex items-center gap-1"><MapPin size={12} className="text-red-500" /> {c.location}</div>
                      <div className="mt-auto pt-4 flex items-center justify-between">
                        <div>
                          <span className="text-2xl font-extrabold text-gray-900">${c.price}</span>
                          <span className="text-sm text-gray-500">{t("cars.perDay")}</span>
                        </div>
                        <button className="pill-btn primary" style={{ padding: "0.5rem 1rem" }} onClick={(e) => { e.stopPropagation(); navigate(`/cars/${c.id}`); }}>
                          {t("cars.bookNow")}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
